import type { KeyboardEvent } from 'react';

import { Icon } from '@shared/ui/icon';
import { Spinner } from '@shared/ui/spinner';
import { TextInput, type TextInputProps } from '@shared/ui/text-input';

import { SEARCH_INPUT_TEXT } from './search-input.text';

export type SearchInputStatus = 'default' | 'loading';

export interface SearchInputProps extends Omit<
  TextInputProps,
  'shape' | 'size' | 'status' | 'type' | 'leading' | 'trailing' | 'invalid'
> {
  /** Figma `status`. loading 이면 돋보기 자리에 Spinner 를 그린다 */
  status?: SearchInputStatus;
  /** Enter 를 누르면 지금 입력값으로 부른다 */
  onSearch?: (value: string) => void;
}

/**
 * 헤더 검색 입력. TextInput pill · md + 오른쪽 돋보기(장식).
 * `filled` 는 값에서, `focus` 는 CSS 에서 정해진다.
 */
export function SearchInput({
  status = 'default',
  onSearch,
  onKeyDown,
  placeholder = SEARCH_INPUT_TEXT.placeholder,
  'aria-label': ariaLabel = SEARCH_INPUT_TEXT.label,
  ...rest
}: SearchInputProps) {
  const loading = status === 'loading';

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      onSearch?.(event.currentTarget.value);
    }
  };

  return (
    <TextInput
      {...rest}
      type="text"
      role="searchbox"
      enterKeyHint="search"
      shape="pill"
      size="md"
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      onKeyDown={handleKeyDown}
      trailing={
        loading ? (
          <Spinner size="sm" tone="neutral" label={SEARCH_INPUT_TEXT.loading} />
        ) : (
          <Icon name="search" size="sm" className="text-fg-muted" />
        )
      }
    />
  );
}
